import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { getOpenStatus } from "@/utils/openStatus";

const HOURS = [
    { day: "Lundi — Samedi", time: "11h30 — 22h30" },
    { day: "Dimanche", time: "Fermé", closed: true },
];

const MAPS_URL =
    "https://www.google.com/maps/search/?api=1&query=180+Av.+Fran%C3%A7ois+Mitterrand+91200+Athis-Mons";

export default function LocationSection() {
    return (
        <section
            id="location"
            data-testid="location-section"
            className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32"
        >
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="font-mono2 mb-4 text-[11px] uppercase tracking-[0.3em] text-rose-400"
            >
                L'Adresse
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl"
            >
                Nous Trouver <span className="text-rose-500">& Horaires</span>
            </motion.h2>

            <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-7"
                >
                    <div
                        data-testid="location-map-card"
                        className="group relative h-full min-h-[380px] overflow-hidden rounded-2xl border border-white/10"
                    >
                        <iframe
                            title="Carte — Petit Veau Athis-Mons"
                            src="https://www.google.com/maps?q=180+Av.+Fran%C3%A7ois+Mitterrand,+91200+Athis-Mons&z=16&output=embed"
                            className="absolute inset-0 h-full w-full border-0 grayscale invert-[0.92] contrast-[0.85] transition-all duration-700 group-hover:invert-[0.85]"
                            loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-[#0A0A0A]/85 px-4 py-2.5 backdrop-blur-md">
                                <span className="animate-pulse-dot h-2 w-2 rounded-full bg-rose-500" />
                                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-200">
                                    Athis-Mons — 91200
                                </span>
                            </div>
                            <motion.a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="location-directions-button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.96 }}
                                className="font-mono2 flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-rose-500"
                            >
                                <Navigation className="h-3.5 w-3.5" />
                                Itinéraire
                            </motion.a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col gap-6 lg:col-span-5"
                >
                    <div className="rounded-2xl border border-white/10 bg-[#18181B] p-7">
                        <div className="flex items-start gap-4">
                            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-rose-600/15 text-rose-500">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    Adresse
                                </p>
                                <p
                                    data-testid="location-address"
                                    className="mt-1.5 text-lg font-semibold leading-snug text-zinc-100"
                                >
                                    180 Av. François Mitterrand,
                                    <br />
                                    91200 Athis-Mons, France
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-start gap-4 border-t border-white/10 pt-6">
                            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-rose-600/15 text-rose-500">
                                <Phone className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    Commandes & Contact
                                </p>
                                <a
                                    href="tel:+33661970727"
                                    data-testid="location-phone"
                                    className="mt-1.5 block text-lg font-semibold text-zinc-100 transition-colors hover:text-rose-400"
                                >
                                    06 61 97 07 27
                                </a>
                            </div>
                        </div>
                    </div>

                    <div
                        data-testid="location-hours-table"
                        className="flex-1 rounded-2xl border border-white/10 bg-[#18181B] p-7"
                    >
                        <div className="mb-5 flex items-center gap-3">
                            <Clock className="h-5 w-5 text-rose-500" />
                            <h3 className="font-display text-lg font-black uppercase tracking-tight">
                                Horaires d'ouverture
                            </h3>
                        </div>
                        <ul className="flex flex-col divide-y divide-white/10">
                            {HOURS.map((h) => (
                                <li
                                    key={h.day}
                                    className="flex items-center justify-between py-3.5"
                                >
                                    <span className="text-sm text-zinc-400">{h.day}</span>
                                    <span
                                        className={`font-mono2 text-sm font-bold ${
                                            h.closed ? "text-rose-500" : "text-zinc-100"
                                        }`}
                                    >
                                        {h.time}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p
                            data-testid="location-open-status"
                            className={`font-mono2 mt-5 flex items-center gap-2 rounded-lg border px-4 py-3 text-[10px] uppercase tracking-[0.2em] ${
                                getOpenStatus().open
                                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                                    : "border-rose-500/25 bg-rose-500/10 text-rose-400"
                            }`}
                        >
                            <span
                                className={`animate-pulse-dot h-1.5 w-1.5 rounded-full ${
                                    getOpenStatus().open ? "bg-emerald-400" : "bg-rose-500"
                                }`}
                            />
                            {getOpenStatus().label} — Lun–Sam 11h30–22h30 · Dimanche fermé
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
